import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import TableHeading from '../../../../components/Table/TableHeading';
import { columnsOrder, headings, statusActionTypes, statusTypes } from '../constants';
import Row from '../../../../components/Table/Row';
import LoadingRows from '../../../../components/Table/LoadingRows';
import NoDataCell from '../../../../components/NoDataCell';
import { pagesText } from '../../../../constants/pagesText';
import Status from './Status';
import { CUSTOMER_QUERY_KEY, USER_QUERY_KEY } from '../../../../constants/query';
import useUpdateUserStatus from '../../../../queries/user/useUpdateUserStatus';
import useQueryData from '../../../../queries/useQueryData';
import { userEmailSelector, userRoleSelector } from '../../../../selectors/user';
import { customerIdSelector } from '../../../../selectors/customer';
import AlertPopup from '../../../../components/AlertPopup';
import AlertDescription from '../../../../components/AlertDescription';
import TableContainer from '../../../../components/Table/TableContainer';
import { formatInviteStatus } from 'src/utils/helpers';

import Button from '../../../../components/Button';
import useResendInvites from '../../../../queries/invites/useResendInvites';
import { useMutation } from 'react-query';
import CustomerService from '../../../../services/customer.service';
import { useSnackbar } from 'notistack';
import GenericTableCell from '../../../../components/GenericTableCell';
import { isDateString } from '../../../../utils/date.util';
import { format } from 'date-fns';
import useDisableMfa from '../../../../queries/user/useDisableMfa';
import { Roles } from 'src/constants/role';

const CompanyMembersTable = forwardRef(({ isLoading, users, isFetching }, ref) => {
  const { enqueueSnackbar } = useSnackbar();
  const disableMfa = useDisableMfa();
  const customerId = useQueryData(CUSTOMER_QUERY_KEY, customerIdSelector);
  const updateUserStatusMutation = useUpdateUserStatus();
  const userEmail = useQueryData(USER_QUERY_KEY, userEmailSelector);
  const userRole = useQueryData(USER_QUERY_KEY, userRoleSelector);
  const { mutateAsync: getInviteCustomerUrl } = useMutation(CustomerService.getInviteCustomerUrl);

  const [resendEmails, setResendEmails] = useState({});
  const resendEmailsCount = useMemo(() => Object.keys(resendEmails).length, [resendEmails]);

  const resendInvitesMutation = useResendInvites();

  const toggleRow = useCallback(email => {
    setResendEmails(prev => {
      const next = { ...prev };
      if (next[email]) delete next[email];
      else next[email] = true;
      return next;
    });
  }, []);

  const handleCopy = useCallback(
    async (event, email, userId) => {
      event.stopPropagation();
      try {
        const url = await getInviteCustomerUrl({ customerId, email, isNew: !userId });

        if (url) {
          await navigator.clipboard.writeText(url);
          enqueueSnackbar('Invite link copied to clipboard!', {
            variant: 'success',
            autoHideDuration: 3000,
            preventDuplicate: true,
          });
        } else {
          enqueueSnackbar('No URL was generated', {
            variant: 'warning',
            autoHideDuration: 3000,
            preventDuplicate: true,
          });
        }
      } catch (error) {
        enqueueSnackbar(error?.message || 'Failed to generate or copy URL', {
          variant: 'error',
          autoHideDuration: 3000,
          preventDuplicate: true,
        });
      }
    },
    [customerId, getInviteCustomerUrl, enqueueSnackbar],
  );

  const onResendInvites = useCallback(() => {
    resendInvitesMutation.mutate(
      { emails: Object.keys(resendEmails), customerId },
      { onSettled: () => setResendEmails({}) },
    );
  }, [resendEmails, customerId, resendInvitesMutation]);

  const renderCustomCell = (column, row) => {
    const isThisRowUpdating =
      updateUserStatusMutation.isLoading &&
      updateUserStatusMutation.variables?.userId === row.userId;

    const isPendingOrExpired = row.status === 'Pending' || row.status === 'Expired';

    if (column === 'checkbox') {
      return (
        <TableCell key="checkbox">
          {isPendingOrExpired && (
            <Box
              className="hover-checkbox"
              sx={{
                opacity: 0.3,
                transition: 'opacity 0.2s',
                mr: 1,
                minWidth: '42px',
                '&:hover': {
                  opacity: 1,
                  '& .MuiSvgIcon-root path': {
                    fill: '#7DFF92',
                  },
                },
              }}>
              <Checkbox
                size="small"
                checked={!!resendEmails[row.email]}
                disabled={resendInvitesMutation.isLoading || isThisRowUpdating}
                onChange={e => {
                  e.stopPropagation();
                  toggleRow(row.email);
                }}
              />
            </Box>
          )}
        </TableCell>
      );
    }

    return (
      <GenericTableCell
        key={row[column]}
        value={
          isDateString(row[column]) ? format(new Date(row[column]), 'yyyy-MM-dd') : row[column]
        }
        styles={{
          wordBreak: 'break-all',
        }}
      />
    );
  };

  return (
    <>
      <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          styles={{ height: 'auto', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
          <Table stickyHeader aria-label="company-members">
            <TableHeading
              headings={headings}
              hasFilter={false}
              hasActions
              listCellStyles={{ top: '0px' }}
            />
            <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
              {isLoading ? (
                <LoadingRows columnCount={4} rowCount={5} />
              ) : (
                <>
                  {!!users?.length &&
                    users.map((row, index) => {
                      const statusType = row.isInvitation
                        ? formatInviteStatus({
                            status: row.status,
                            expirationDate: row.expirationDate,
                          })
                        : row.isActive
                          ? statusTypes.active
                          : statusTypes.suspended;

                      const isThisRowUpdating =
                        updateUserStatusMutation.isLoading &&
                        updateUserStatusMutation.variables?.userId === row.userId;

                      const actionText = row.isActive
                        ? statusActionTypes.suspended
                        : statusActionTypes.active;

                      return (
                        <Row
                          ref={index === users.length - 1 ? ref : null}
                          key={row.id}
                          row={{ ...row, status: statusType }}
                          columnData={[...columnsOrder]}
                          hasSelectOption={false}
                          markedRows={resendEmails}
                          renderCustomCell={renderCustomCell}
                          setMarkedRows={() => {}}
                          changeMarkedItem={() => {}}
                          ActionComponent={() => (
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                }}>
                                {userRole === Roles.superAdmin && (
                                  <Button
                                    size="small"
                                    variant="Untrusted"
                                    disabled={!row.twoFactorEnabled || disableMfa.isLoading}
                                    onClick={() => disableMfa.mutate(row.userId)}
                                    sx={{
                                      mr: 1,
                                      minWidth: '112px', //keeps button similar to Status element (styled button)
                                      height: '34px',
                                    }}>
                                    {pagesText.companyUsers.disableMfaButton}
                                  </Button>
                                )}
                                {!row.isInvitation && (
                                  <Status
                                    loading={isThisRowUpdating}
                                    disabled={userEmail === row.email || isThisRowUpdating}
                                    onClick={event => {
                                      event.stopPropagation();
                                      updateUserStatusMutation.mutate({
                                        userId: row.userId,
                                        customerId,
                                      });
                                    }}>
                                    {actionText}
                                  </Status>
                                )}
                                {statusType === 'Pending' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={e => handleCopy(e, row.email, row.userId)}>
                                    Copy Invite URL
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          )}
                        />
                      );
                    })}
                  {!isLoading && isFetching && <LoadingRows columnCount={4} rowCount={5} />}
                  {!users?.length && !!customerId && (
                    <NoDataCell colSpan={5}>{pagesText.noDataFound}</NoDataCell>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!customerId && !users?.length && !isLoading && (
          <Box mt={1} mb={1}>
            <AlertPopup title={pagesText.companyList.noCompanyAlert.title}>
              <AlertDescription>
                {pagesText.companyList.noCompanyAlert.description}
              </AlertDescription>
              <AlertDescription>{pagesText.appsStarterPopup.extraDescription}</AlertDescription>
            </AlertPopup>
          </Box>
        )}
      </Paper>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          loading={resendInvitesMutation.isLoading}
          disabled={!resendEmailsCount || isLoading || resendInvitesMutation.isLoading}
          onClick={onResendInvites}
          variant="outlined"
          styles={{ textTransform: 'capitalize', minHeight: '40px' }}>
          {`${pagesText.inviteUsers.resendInvitesButton} ${resendEmailsCount}`}
        </Button>
      </Stack>
    </>
  );
});

export default memo(CompanyMembersTable);
